import React from 'react'
import { useInvoiceStore } from '../invoiceStore'
import { calculateInvoiceTotals } from '../../utils/invoiceCalculations'
import type { PaymentStatus } from '@dutchinvoice/types'
import { isFutureDate } from '@dutchinvoice/shared-utils'
import type { SyncedInvoice } from '../../types/invoice'

// Utility function to check if any invoice has meaningful content
export const invoiceHasContent = (invoice: SyncedInvoice): boolean => {
    const freshInvoice = useInvoiceStore.getState().newInvoice()

    // Compare key fields that indicate user modifications beyond defaults
    const hasModifiedSender = invoice.sender !== freshInvoice.sender &&
        JSON.stringify(invoice.sender) !== JSON.stringify(freshInvoice.sender)
    const hasModifiedRecipient = JSON.stringify(invoice.recipient) !== JSON.stringify(freshInvoice.recipient)
    const hasModifiedItems = JSON.stringify(invoice.items) !== JSON.stringify(freshInvoice.items)
    const hasModifiedNotes = invoice.notes !== freshInvoice.notes
    const hasModifiedDiscount = JSON.stringify(invoice.discount) !== JSON.stringify(freshInvoice.discount)

    return hasModifiedSender || hasModifiedRecipient || hasModifiedItems || hasModifiedNotes || hasModifiedDiscount
}

// Selector hooks for better performance
export const useCurrentInvoice = () => useInvoiceStore((state) => state.currentInvoice)
export const useEditingSection = () => useInvoiceStore((state) => state.editingSection)
export const useSavedInvoices = () => useInvoiceStore((state) => state.savedInvoices)

// Selector for filtered invoices
export const useFilteredInvoices = (paymentStatusFilter: string = 'all') => {
    const allInvoices = useInvoiceStore((state) => state.savedInvoices)
    const currentInvoice = useInvoiceStore((state) => state.currentInvoice)
    const getFilteredInvoicesByPeriod = useInvoiceStore((state) => state.getFilteredInvoicesByPeriod)
    const hasContent = useInvoiceHasContent()

    // Combine current draft with saved invoices if it has any non-placeholder values
    const combinedInvoices = React.useMemo(() => {
        const shouldIncludeCurrent = hasContent &&
            !allInvoices.some(inv => inv.invoiceNumber === currentInvoice.invoiceNumber);

        return shouldIncludeCurrent
            ? [currentInvoice, ...allInvoices]
            : allInvoices;
    }, [currentInvoice, allInvoices, hasContent])

    // First filter by period, then by payment status
    return React.useMemo(() => {
        // First filter by period
        const periodFiltered = getFilteredInvoicesByPeriod(combinedInvoices)

        // Then filter by payment status or planned
        return periodFiltered.filter(invoice => {
            // Special handling for draft/concept invoices
            if (!invoice.invoiceNumber) {
                // For planned view, only show drafts if they have a scheduleId or future date
                if (paymentStatusFilter === 'planned') {
                    return isFutureDate(invoice.date) || !!invoice.scheduleId;
                }
                // For other views, always show drafts
                return true;
            }

            if (paymentStatusFilter === 'planned') {
                // Show both:
                // 1. Regular invoices with future dates
                // 2. Invoices that have scheduleIds (template invoices)
                return isFutureDate(invoice.date) || !!invoice.scheduleId
            }

            return paymentStatusFilter === 'all' || invoice.paymentStatus === paymentStatusFilter as PaymentStatus
        })
    }, [combinedInvoices, paymentStatusFilter, getFilteredInvoicesByPeriod])
}

// Selector for detecting if current invoice has meaningful content
export const useInvoiceHasContent = () => {
    const currentInvoice = useCurrentInvoice()

    return React.useMemo(() => {
        return invoiceHasContent(currentInvoice);
    }, [currentInvoice])
}

export const useInvoiceCalculations = () => {
    const currentInvoice = useInvoiceStore(
        React.useCallback((state) => state.currentInvoice, [])
    )

    // Ensure we have valid data
    const items = React.useMemo(() =>
        Array.isArray(currentInvoice?.items) ? currentInvoice.items : [],
        [currentInvoice?.items]
    )

    const discount = React.useMemo(() =>
        currentInvoice?.discount ?? { type: 'none', value: '0' },
        [currentInvoice?.discount]
    )

    // Memoize calculations to prevent unnecessary recalculations
    return React.useMemo(() =>
        calculateInvoiceTotals(items, discount),
        [items, discount]
    )
} 